import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDateNow, useReduxState } from 'hooks';
import { isUserAllowedToRequestIndividualAchievements } from 'shared-helpers/achievements';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  DisplayAttachments,
  EmptyIndicator,
  FlexContainer,
  ModalButton,
  MoreOptions,
  Pagination,
  TextWithLineBreaks,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import { FiltersProvider } from 'components/common/filter';
import { StatCount } from 'components/common/stats';
import {
  AchievementsIndividualAccessRequests,
  AchievementsIndividualCreate,
  AchievementsIndividualAddOrRequest,
  AchievementsIndividualDelete,
  AchievementsIndividualEdit,
  AchievementsIndividualExport,
  AchievementsIndividualSubHeader,
  AchievementsIndividualViewInfoModal,
  AchievementRenderAddedBy
} from '.';

import type { SortIndividualAchievements } from 'shared-types';
import type { GetIndividualAchievementsRes } from 'shared-types/responses';
import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';
import type { ButtonInterface } from 'components/common/Button';
import type { IndividualAchievementFieldsView } from './AchievementsIndividualSubHeader';

const AchievementsIndividual = () => {
  const { dateNow: now } = useDateNow();

  const { messages, serverConfig, theme, user } = useReduxState();

  const accessRequestsUpdateHandler = useRef<ApiDataHookUpdateHandler>(null);

  const router = useRouter();

  const page = parseInt(router.query.page as string) || 1;

  const [sortBy, setSortBy] =
    useState<SortIndividualAchievements['sortBy']>('completionDate');
  const [view, setView] = useState<IndividualAchievementFieldsView>('default');

  let queryKeys: string[] = [];
  if (user?.roles.includes('admin')) {
    queryKeys = Object.keys(router.query || {}).filter(
      (key) => key !== 'type' && key !== 'page'
    );
  }

  const { data, loading, updateData } =
    useApiData<GetIndividualAchievementsRes>({
      url:
        `/admin/achievements-individual?page=${page}&sortBy=${sortBy}` +
        queryKeys.map((key) => `&${key}=${router.query[key]}`).join('')
    });

  const handleGetUpdateHandler = (updateFunc: () => Promise<void>) => {
    accessRequestsUpdateHandler.current = updateFunc;
  };

  const infoButtonProps = {
    category: 'lighter',
    iconStyle: { fontSize: '1.1rem' },
    size: 'small',
    style: {
      borderRadius: 20,
      fontSize: '1.2rem',
      fontWeight: 600,
      padding: '3px 9px 4px 7px'
    }
  } as ButtonInterface;

  const isAllFieldsView = view === 'all fields';
  const isDefaultView = view === 'default';
  const isLoggedUserSuperadmin = user?.roles.includes('superadmin');

  const mainContent = (
    <div className="individual-achievements">
      {serverConfig.individualAchievementsUserRequireApproval && (
        <AchievementsIndividualAccessRequests
          getUpdateHandler={handleGetUpdateHandler}
          updateUsers={updateData}
        />
      )}

      <div>
        <Box
          headerContent={
            <FlexContainer style={{ gap: 10 }}>
              {isLoggedUserSuperadmin ? (
                <AchievementsIndividualCreate updateData={updateData} />
              ) : !isLoggedUserSuperadmin &&
                isUserAllowedToRequestIndividualAchievements({
                  serverConfig,
                  user
                }) ? (
                <AchievementsIndividualAddOrRequest
                  updateMainViewData={updateData}
                  updatePendingData={accessRequestsUpdateHandler.current}
                />
              ) : null}

              <MoreOptions category="lighter" styleContentButtons={false}>
                <AchievementsIndividualExport
                  individualAchievements={data?.individualAchievements}
                  titleSearch={(router.query?.titleSearch || '') as string}
                  sortBy={sortBy}
                />

                {isLoggedUserSuperadmin ? (
                  <AchievementsIndividualDelete updateData={updateData} />
                ) : null}
              </MoreOptions>
            </FlexContainer>
          }
          headerContentWrap={true}
          loading={loading}
          padding={0}
          subHeaderContent={
            <AchievementsIndividualSubHeader
              setSortBy={setSortBy}
              setView={setView}
              sortBy={sortBy}
              view={view}
            />
          }
          title={
            <>
              {messages.individualAchievements}
              <StatCount
                isLarge={true}
                style={{ display: 'inline-block', marginLeft: 5 }}
                value={data?.numberOfIndividualAchievements || 0}
              />
            </>
          }
        >
          {data?.individualAchievements?.length ? (
            <BoxOverflowWrapper>
              <BoxTable>
                <BoxTableHeader
                  style={{ whiteSpace: 'nowrap' }}
                  withCheckbox={true}
                  withCheckboxIdentifier="individual-achievements"
                >
                  <BoxTableCell sticky={true}>
                    {messages.commonTitle}
                  </BoxTableCell>

                  <BoxTableCell>{messages.commonUser}</BoxTableCell>

                  {isAllFieldsView ? (
                    <>
                      <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>
                      <BoxTableCell>{messages.commonStartDate}</BoxTableCell>
                    </>
                  ) : null}

                  <BoxTableCell>
                    {messages.completionsCompletionDate}
                  </BoxTableCell>

                  <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>

                  {isAllFieldsView ? (
                    <>
                      <BoxTableCell>{messages.excelWorkload}</BoxTableCell>
                      <BoxTableCell>{messages.commonComment}</BoxTableCell>
                      <BoxTableCell>{messages.commonAttachments}</BoxTableCell>
                      {serverConfig.individualAchievementCustomAttributes
                        .length > 0
                        ? serverConfig.individualAchievementCustomAttributes.map(
                            (attribute) => (
                              <BoxTableCell key={attribute.key}>
                                {attribute.title}
                              </BoxTableCell>
                            )
                          )
                        : null}
                    </>
                  ) : null}

                  {isDefaultView ? <BoxTableCell shrink={true} /> : null}

                  <BoxTableCell
                    shrink={true}
                    sticky={true}
                    stickyAnchor="right"
                  />
                </BoxTableHeader>

                {data?.individualAchievements?.map((individualAchievement) => (
                  <BoxTableRow
                    key={individualAchievement.id}
                    withCheckboxValue={individualAchievement.id}
                  >
                    <BoxTableCell
                      sticky={true}
                      stickyBackgroundColor="white"
                      style={{
                        minWidth: 200,
                        maxWidth: 245,
                        width: 200,
                        wordBreak: 'break-word'
                      }}
                    >
                      {individualAchievement.title}
                    </BoxTableCell>

                    <BoxTableCell>
                      <User
                        enableProfileLink={true}
                        style={{ minWidth: 200 }}
                        user={individualAchievement.User}
                      />
                    </BoxTableCell>

                    {isAllFieldsView ? (
                      <>
                        <BoxTableCell>
                          {individualAchievement.AddedByUserId ? (
                            <AchievementRenderAddedBy
                              addedBy={{
                                ...individualAchievement.AddedByUser,
                                addedByType: 'user'
                              }}
                              enableLink={true}
                              style={{ minWidth: 160 }}
                            />
                          ) : (
                            '-'
                          )}
                        </BoxTableCell>
                        <BoxTableCell>
                          {individualAchievement.startedAtDate
                            ? getFormattedDate({
                                date: individualAchievement.startedAtDate
                              })
                            : '-'}
                        </BoxTableCell>
                      </>
                    ) : null}

                    <BoxTableCell>
                      <span
                        className="date-completed"
                        style={
                          individualAchievement.completionDate &&
                          Date.parse(
                            individualAchievement.completionDate as string
                          ) > now
                            ? { color: theme.warningStandaloneColor }
                            : {}
                        }
                      >
                        {individualAchievement.completionDate
                          ? getFormattedDate({
                              date: individualAchievement.completionDate
                            })
                          : '-'}
                      </span>
                    </BoxTableCell>

                    <BoxTableCell>
                      <span
                        className={`${
                          Date.parse(
                            individualAchievement.expirationDate as string
                          ) < now
                            ? 'date-expired'
                            : ''
                        }`}
                      >
                        {individualAchievement.expirationDate
                          ? getFormattedDate({
                              date: individualAchievement.expirationDate
                            })
                          : '-'}
                      </span>
                    </BoxTableCell>

                    {isAllFieldsView ? (
                      <>
                        <BoxTableCell>
                          {individualAchievement.workload
                            ? individualAchievement.workload
                            : '-'}
                        </BoxTableCell>
                        <BoxTableCell>
                          {individualAchievement.comments ? (
                            <ModalButton
                              icon="comment"
                              modalProps={{
                                closeButton: true,
                                title: messages.commonComment,
                                width: 'medium'
                              }}
                              text={messages.commonComment}
                              {...infoButtonProps}
                            >
                              <div className="comment">
                                <TextWithLineBreaks
                                  text={individualAchievement.comments}
                                />
                              </div>
                            </ModalButton>
                          ) : (
                            '-'
                          )}
                        </BoxTableCell>
                        <BoxTableCell>
                          <DisplayAttachments
                            attachments={individualAchievement.attachments}
                            modalButtonProps={infoButtonProps}
                          />
                        </BoxTableCell>
                        {serverConfig.individualAchievementCustomAttributes
                          .length > 0
                          ? serverConfig.individualAchievementCustomAttributes.map(
                              (attribute) => (
                                <BoxTableCell
                                  key={attribute.key}
                                  style={{ minWidth: 160 }}
                                >
                                  {individualAchievement.customAttributes?.[
                                    attribute.key
                                  ] ? (
                                    <AttributeDisplayValue
                                      value={
                                        individualAchievement.customAttributes[
                                          attribute.key
                                        ]
                                      }
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </BoxTableCell>
                              )
                            )
                          : null}
                      </>
                    ) : null}

                    {isDefaultView ? (
                      <BoxTableCell shrink={true}>
                        <AchievementsIndividualViewInfoModal
                          individualAchievement={individualAchievement}
                          user={individualAchievement.User}
                        />
                      </BoxTableCell>
                    ) : null}

                    <BoxTableCell
                      shrink={true}
                      sticky={true}
                      stickyAnchor="right"
                      stickyBackgroundColor="white"
                    >
                      {!individualAchievement.displayOnly ? (
                        <MoreOptions renderInPortal={true}>
                          <AchievementsIndividualEdit
                            individualAchievement={individualAchievement}
                            updateData={updateData}
                          />
                          <AchievementsIndividualDelete
                            individualAchievement={individualAchievement}
                            updateData={updateData}
                          />
                        </MoreOptions>
                      ) : null}
                    </BoxTableCell>
                  </BoxTableRow>
                ))}
              </BoxTable>
            </BoxOverflowWrapper>
          ) : (
            <EmptyIndicator
              icon="star"
              title={messages.achievementsIndividualNotFound}
            />
          )}
        </Box>

        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => {
            router.replace({ query: { ...router.query, page } });
          }}
        />
      </div>

      <style jsx>{`
        .individual-achievements {
          display: flex;
          flex-direction: column;
          gap: 40px;
        }
        .date-completed {
          color: ${theme.successStandaloneColor};
        }
        .date-expired {
          color: ${theme.dangerColor};
        }
      `}</style>
    </div>
  );

  // only wrap content with FiltersProvider for admin users
  return user?.roles.includes('admin') ? (
    <FiltersProvider
      filters={{
        supervisors: true,
        userAttributes: true,
        groups: true
      }}
    >
      {mainContent}
    </FiltersProvider>
  ) : (
    mainContent
  );
};

export default AchievementsIndividual;
